import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { FormConfig } from './config/steps-config';
import { NameApiProvider } from '../../enums/name-api-provider';
import { AcademyCoursesService } from '../courses.service';
import { LocalStorageService } from 'app/services/local-storage.service';
import { FrontConfiguration } from 'app/config/front-configuration';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { checkCurrentPartner } from 'app/shared/util/common-functions';
import { FrontPartnersIds } from 'app/config/front-partners-ids';

@Injectable()
export class RoomDetailService implements Resolve<any> {
  onLoadChannels: BehaviorSubject<any[]>;
  onChangeMatStepper: BehaviorSubject<'next' | 'previous' | ''>;
  onCurrentChannel: BehaviorSubject<any>;
  onErrorToSubmit: BehaviorSubject<boolean>;
  onResetMainFormGroup: BehaviorSubject<boolean>;
  onCurrentRoom: BehaviorSubject<any>;
  onActionIsEdit: BehaviorSubject<boolean>;
  onShowPreLoading: BehaviorSubject<boolean>;
  routeParams: any;
  currentUser: any;

  // Set the private defaults
  private hasBlockNavigation: boolean = false;

  constructor(
    private _coursesService: AcademyCoursesService,
    private _localStorageService: LocalStorageService,
    private _translate: TranslateService,
    private _httpClient: HttpClient
  ) {
    // Set the defaults
    this.setSubjectWithDefaultValue();
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    this.routeParams = route.params;
    this.currentUser = this.getCurrentUser();

    return new Promise((resolve, reject) => {
      // Edit Room
      if (this.routeParams.id) {
        // Get Room by Id
        this._coursesService.getRoomById(this.routeParams.id).subscribe(
          (room: any) => {
            // Get Users Assigned to Room
            this.getUsersAssignedToEditRoom(room, 0, 20).subscribe(
              (users: any) => {
                // Set the current action is edition
                this.onActionIsEdit.next(true);
                // Set current Channel
                this.setCurrentChannel(this.fillChannelInfo(room.channel));
                // Set current Room
                this.setCurrentRoom({
                  ...room,
                  users,
                });
                resolve(null);
              },
              err => {
                reject();
              }
            );
          },
          err => {
            reject();
          }
        );
      } else {
        // Create Room
        // Get Channels by config
        this._coursesService.getChannelsByPartnerConfig().subscribe(
          channels => {
            // Set the current action is edition
            this.onActionIsEdit.next(false);
            this.onLoadChannels.next(channels);
            resolve(null);
          },
          err => {
            reject();
          }
        );
      }
    });
  }

  setSubjectWithDefaultValue() {
    this.onLoadChannels = new BehaviorSubject([]);
    this.onChangeMatStepper = new BehaviorSubject('');
    this.onCurrentChannel = new BehaviorSubject('');
    this.onErrorToSubmit = new BehaviorSubject(false);
    this.onResetMainFormGroup = new BehaviorSubject(false);
    this.onCurrentRoom = new BehaviorSubject(null);
    this.onActionIsEdit = new BehaviorSubject(false);
    this.onShowPreLoading = new BehaviorSubject(false);
    this.setBlockNavigation(false); // block navigation-reset value
  }

  getCurrentUser() {
    return this._localStorageService.getCurrentUser();
  }

  changeMatStepper(step: 'next' | 'previous' | '') {
    this.onChangeMatStepper.next(step);
  }

  setCurrentChannel(channel: any) {
    this.onCurrentChannel.next(channel);
  }

  setErrorToSubmit(isError: boolean) {
    this.onErrorToSubmit.next(isError);
  }

  setResetMainFormGroup(init: boolean) {
    this.onResetMainFormGroup.next(init);
  }

  setCurrentRoom(room: any) {
    this.onCurrentRoom.next(room);
  }

  getEmptyListMessageKey(): string {
    const isDoppler = checkCurrentPartner(FrontPartnersIds.PARTNER_ID_DOPPLER);
    return isDoppler
      ? this._translate.instant('CARD_WHATSAPP_MARKETING')
      : this._translate.instant('CARD_WHATSAPP_API_CLOUD');
  }

  fillChannelInfo(channel): any {
    const channelId = channel.id;
    const channelInfo = {
      ...channel,
      imageUrl: null,
      title: null,
      apiProvider: null,
      disabled: false,
      description: '',
      channelConfig: [],
      buyChannelConfig: [],
    };
    if (channelId === 1) {
      channelInfo.imageUrl = '/assets/images/rooms/Whatsapp_generic.png';
      channelInfo.title = this.getEmptyListMessageKey();
      channelInfo.apiProvider = NameApiProvider.WHATSAPP_API_CLOUD;
      channelInfo.disabled = false;
      channelInfo.description = this._translate.instant(
        'CHANNEL.WHATSAPP_DESCRIPTION'
      );
      channelInfo.channelConfig = [FrontConfiguration.WHATSAPP_API_CHANNEL];
      channelInfo.buyChannelConfig = [FrontConfiguration.BUY_WHATSAPP_API_CHANNEL];
      return channelInfo;
    }
    // TODO: Channel not implemented
    /* if (channelId === 2) {
            channelInfo.imageUrl = '/assets/images/rooms/twitter_generic.png';
            channelInfo.title = channel.name;
            channelInfo.apiProvider = null;
            return channelInfo
        } */
    if (channelId === 3) {
      channelInfo.imageUrl = '/assets/images/rooms/roomWeb_generic.png';
      channelInfo.title = 'Chat Web';
      channelInfo.apiProvider = NameApiProvider.BEPLIC;
      channelInfo.disabled = false;
      channelInfo.description = this._translate.instant(
        'CHANNEL.WEBBOT_DESCRIPTION'
      );
      channelInfo.channelConfig = [FrontConfiguration.WEBCHAT_CHANNEL];
      channelInfo.buyChannelConfig = [FrontConfiguration.BUY_WEBCHAT_CHANNEL];
      return channelInfo;
    }
    if (channelId === 4) {
      channelInfo.imageUrl = '/assets/images/rooms/messenger_generic.png';
      channelInfo.title = channel.name;
      channelInfo.apiProvider = NameApiProvider.FACEBOOK;
      channelInfo.disabled = false;
      channelInfo.description = this._translate.instant(
        'CHANNEL.MESSENGER_DESCRIPTION'
      );
      channelInfo.channelConfig = [FrontConfiguration.MESSENGER_CHANNEL];
      channelInfo.buyChannelConfig = [FrontConfiguration.BUY_MESSENGER_CHANNEL];
      return channelInfo;
    }
    if (channelId === 5) {
      channelInfo.imageUrl = '/assets/images/rooms/email_generic.png';
      channelInfo.title = channel.name;
      channelInfo.apiProvider = null;
      channelInfo.disabled = true;
      channelInfo.description = this._translate.instant('CHANNEL.EMAIL_DESCRIPTION');
      channelInfo.channelConfig = [FrontConfiguration.EMAIL_CHANNEL];
      channelInfo.buyChannelConfig = [FrontConfiguration.BUY_EMAIL_CHANNEL];
      return channelInfo;
    }
    if (channelId === 6) {
      channelInfo.imageUrl = '/assets/images/rooms/instagram_generic.png';
      channelInfo.title = channel.name;
      channelInfo.apiProvider = NameApiProvider.INSTAGRAM;
      channelInfo.disabled = false;
      channelInfo.description = this._translate.instant(
        'CHANNEL.INSTAGRAM_DESCRIPTION'
      );
      channelInfo.channelConfig = [FrontConfiguration.IG_CHANNEL];
      channelInfo.buyChannelConfig = [FrontConfiguration.BUY_IG_CHANNEL];
      return channelInfo;
    }
    //Add Whatsapp lite
    if (channelId === 7) {
      channelInfo.imageUrl = '/assets/images/rooms/whatsapp_lite.png';
      channelInfo.title = 'WhatsApp Lite';
      channelInfo.apiProvider = NameApiProvider.WHATSAPP_LITE_API;
      channelInfo.disabled = false;
      channelInfo.description = this._translate.instant(
        'CHANNEL.WHATSAPP_LITE_DESCRIPTION'
      );
      channelInfo.channelConfig = [FrontConfiguration.WHATSAPP_LITE_API_CHANNEL];
      channelInfo.buyChannelConfig = [
        FrontConfiguration.BUY_WHATSAPP_LITE_API_CHANNEL,
      ];
      return channelInfo;
    }
  }

  addValidatorsInControllers(stepConfig: FormConfig, form: FormGroup) {
    //this.cleanValidators(form);
    if (!(stepConfig && stepConfig.controlsValidator)) return;

    stepConfig.controlsValidator.forEach(ctrlValidator => {
      const control = form.controls[ctrlValidator.name];
      if (control && ctrlValidator.validators) {
        control.setValidators(this.createValidators(ctrlValidator.validators));
        control.updateValueAndValidity();
      }
    });
  }

  createValidators(ctrlValidators: any[]) {
    if (!ctrlValidators) return [];
    return ctrlValidators.map((vn: any) => {
      let validator = null;
      if (vn.type === 'required') validator = Validators[vn.type];
      if (vn.type === 'pattern' && vn.regExp)
        validator = Validators.pattern(new RegExp(vn.regExp));

      return validator;
    });
  }

  cleanValidators(form: FormGroup) {
    for (const key in form.controls) {
      form.get(key).clearValidators();
      form.get(key).clearAsyncValidators();
      //form.get(key).updateValueAndValidity();
    }
  }

  buildCustomerUserObj(room: any) {
    if (!room) return;
    return { customersId: this.currentUser.customerId, room };
  }

  getUsersAssignedToEditRoom(room: any, page?: number, size?: number) {
    return this._coursesService
      .getUsersAssignedToRoom(this.buildCustomerUserObj(room), page, size)
      .pipe(
        // Filter customerUser other than the current logged in user
        map((customerUserRooms: any[]) =>
          customerUserRooms.filter(
            customerUserRoom =>
              customerUserRoom.customeruserId !== this.currentUser.id
          )
        )
      );
  }

  getUsersAssignedToCreateRoom(customerId: number, page?: number, size?: number) {
    return this._coursesService
      .getCustomerUsersByIdCustomer(customerId, page, size)
      .pipe(
        // Filter customerUser other than the current logged in user
        map((customerUserRooms: any[]) =>
          customerUserRooms.filter(
            customerUserRoom => customerUserRoom.id !== this.currentUser.id
          )
        )
      );
  }

  checkIfChannelWasChanged(currentChannel: any, selectedChannel: any) {
    return !currentChannel ||
      (currentChannel && currentChannel.id !== selectedChannel.id)
      ? true
      : false;
  }

  setBlockNavigation(isBlocked: boolean) {
    this.hasBlockNavigation = isBlocked;
  }

  getBlockNavigation() {
    return this.hasBlockNavigation;
  }

  /**
   * Create room in server ws lite
   * @returns
   */
  createRoomWSLite(idCustmer: any) {
    return this._coursesService.createRoomWSLite(idCustmer);
  }

  findStatusRoomWsLite(uuid: any) {
    return this._coursesService.findQrRoomWsLite(uuid);
  }

  addChannelsByPartnerConfig(currentStepConfig: any, currentChannel: any): void {
    this._coursesService.getChannelsByPartnerConfig().subscribe(data => {
      data.forEach(element => {
        if (element.id == currentChannel.id && currentStepConfig.botConfiguration) {
          currentStepConfig.botConfiguration.show =
            currentStepConfig.botConfiguration ? element.botConfiguration : false;
        }
      });
    });
  }

  getChatWebStyleConfig(appSecretPass: string) {
    return this._httpClient.get(
      '/services/bepliccoreroom/api/web-chat-visual-config?appSecretPass=' +
        appSecretPass
    );
  }

  saveChatWebStyleConfig(roomId: number, config: any) {
    const body = {
      roomId,
      config,
    };

    return this._httpClient.put(
      '/services/bepliccoreroom/api/web-chat-visual-config',
      body
    );
  }

  uploadToS3(file: File, type: 'avatar' | 'launcher'): Promise<string> {
    const formData = new FormData();
    formData.append('multipartFile', file);
    // formData.append('type', type); // opcional, si tu backend lo usa para organizar

    return this._httpClient
      .post('/services/bepliccorefile/api/uploadFile', formData)
      .toPromise()
      .then((res: any) => res.url)
      .catch(err => {
        console.error(`Error al subir archivo ${type}:`, err);
        throw new Error(`Error al subir la imagen del ${type}`);
      });
  }
}
